import React from "react";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const useExportBillOfMaterialInExcel = () => {

    const exportExcel = (rawMaterialRows, chemicalMaterialRows,fuelMaterialRows) => {
        const wb = XLSX.utils.book_new();


        // summary of raw material 
        const summary = rawMaterialRows.reduce(
            (acc, row) => {
                row.layers.forEach((layer, i) => {
                    acc.furnish[i] += parseFloat(layer.furnish) || 0;
                    acc.tonsPerTon[i] += parseFloat(layer.tonsPerTon) || 0;
                    acc.totalTon[i] += parseFloat(layer.totalTon) || 0;
                });
                return acc;
            },
            { furnish: [0, 0, 0, 0], tonsPerTon: [0, 0, 0, 0], totalTon: [0, 0, 0, 0] }
        );

        // Calculate summary values for each layer
        const chemicalSummary = chemicalMaterialRows.reduce(
            (acc, row) => {
                row.layers.forEach((layer, i) => {
                    acc.dosage[i] += parseFloat(layer.dosage) || 0;
                    acc.kgPerTon[i] += parseFloat(layer.kgPerTon) || 0;
                    acc.totalTon[i] += parseFloat(layer.totalTon) || 0;
                });
                return acc;
            },
            { dosage: [0, 0, 0, 0], kgPerTon: [0, 0, 0, 0], totalTon: [0, 0, 0, 0] }
        );

            // Calculate summary values for each layer
        const fuelSummary = fuelMaterialRows.reduce(
            (acc, row) => {
                row.layers.forEach((layer, i) => {
                    acc.dosage[i] += parseFloat(layer.dosage) || 0;
                    acc.kgPerTon[i] += parseFloat(layer.kgPerTon) || 0;
                    acc.totalTon[i] += parseFloat(layer.totalTon) || 0;
                });
                return acc;
            },
            { dosage: [0, 0, 0, 0], kgPerTon: [0, 0, 0, 0], totalTon: [0, 0, 0, 0] }
        );

        // Extract raw material data
        const rawMaterialData = rawMaterialRows.map((row, index) => [
            index + 1,
            row.material ? `${row.material.material_code} - ${row.material.material_name}` : "",
            row.yield,
            ...row.layers.flatMap(layer => [layer.furnish, layer.tonsPerTon, layer.totalTon])
        ]);

        // Add Summary Row
        const summaryRow = [
            "", "Summary (Total)", "",  // First 3 columns (S.No, Material, Yield)
            ...summary.furnish.flatMap((total, layerIndex) => [
                total.toFixed(2),
                summary.tonsPerTon[layerIndex].toFixed(2),
                summary.totalTon[layerIndex].toFixed(2)
            ])
        ];
        rawMaterialData.push(summaryRow); // Append summary row


        // Add summary column


        // Extract chemical data
        const chemicalData = chemicalMaterialRows.map((row, index) => [
            index + 1,
            row.material ? `${row.material.material_code} - ${row.material.material_name}` : "",
            row.yield,
            ...row.layers.flatMap(layer => [layer.dosage, layer.kgPerTon, layer.totalTon])
        ]);

        // Add Chemical Summary Row
        const chemicalSummaryRow = [
            "", "Summary (Total)", "",
            ...chemicalSummary.totalTon.map(total => total.toFixed(2))
        ];
        chemicalData.push(chemicalSummaryRow);


        const fuelData = fuelMaterialRows.map((row, index) => [
            index + 1,
            row.material ? `${row.material.material_code} - ${row.material.material_name}` : "",
            row.yield,
            ...row.layers.flatMap(layer => [layer.dosage, layer.kgPerTon, layer.totalTon])
        ]);

        // Add Chemical Summary Row
        const fuelSummaryRow = [
            "", "Summary (Total)", "",
            ...fuelSummary.totalTon.map(total => total.toFixed(2))
        ];
        fuelData.push(fuelSummaryRow);

        // Define headers for both tables
        const rawMaterialHeaders = [
            "S.No", "Raw Material", "Yield",
            "TOP Furnish", "TOP tons/ton", "TOP Total Tons",
            "Under Top Furnish", "Under Top tons/ton", "Under Top Total Tons",
            "Filler Furnish", "Filler tons/ton", "Filler Total Tons",
            "Back Furnish", "Back tons/ton", "Back Total Tons"
        ];

        const chemicalHeaders = ["S.No", "Chemicals", "Yield",
            "TOP Dosage", "TOP Kg/ton", "TOP Total Tons",
            "Under Top Dosage", "Under Top Kg/ton", "Under Top Total Tons",
            "Filler Dosage", "Filler Kg/ton", "Filler Total Tons",
            "Back Dosage", "Back Kg/ton", "Back Total Tons"];

        const fuelHeaders = ["S.No", "Fuels", "Yield",
            "TOP Dosage", "TOP Kg/ton", "TOP Total Tons",
            "Under Top Dosage", "Under Top Kg/ton", "Under Top Total Tons",
            "Filler Dosage", "Filler Kg/ton", "Filler Total Tons",
            "Back Dosage", "Back Kg/ton", "Back Total Tons"];            

        // Create worksheet and add both tables
        const ws = XLSX.utils.aoa_to_sheet([
            rawMaterialHeaders,
            ...rawMaterialData,
            [],
            chemicalHeaders,
            ...chemicalData,
            [],
            fuelHeaders,
            ...fuelData
        ]);

        // Auto size columns
        ws['!cols'] = rawMaterialHeaders.map(() => ({ wch: 20 }));

        // Define a border style
        const borderStyle = {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" }
        };

        // Apply borders to all filled cells
        const range = XLSX.utils.decode_range(ws["!ref"]);
        for (let R = range.s.r; R <= range.e.r; ++R) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
                if (!ws[cellAddress]) continue;
                if (!ws[cellAddress].s) ws[cellAddress].s = {}; // Ensure cell style exists
                ws[cellAddress].s.border = borderStyle;
            }
        }

        // Append the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, "BOM & Chemicals");

        // Write and save file
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, "BOM_details.xlsx");
    }

    return { exportExcel, };



}
export default useExportBillOfMaterialInExcel;